/**
 * Esqueleto geográfico de uma experiência curada linear (módulo puro).
 *
 * Genérico por TIPO, nunca por destino: qualquer template com âncoras
 * ordenadas (estrada, comboio panorâmico, trilho, barco) produz um esqueleto
 * dia-a-dia que avança sempre no mesmo sentido, sem repetir cidades como
 * enchimento e terminando obrigatoriamente na última âncora quando o modo é
 * "percurso completo".
 */

import type { CuratedRouteTemplate } from "../catalog/curated-route-templates";
import { fold } from "../geo-scope";

export type RouteSkeletonLeg = {
  day: number;
  city: string;
  /** Cidade de onde se parte nesse dia (dia 1 não tem). */
  from?: string;
  approxKm?: number;
  segmentName?: string;
};

export type RouteSkeleton = {
  templateId: string;
  mode: "full" | "partial";
  cities: string[];
  legs: RouteSkeletonLeg[];
  /** Transporte imposto pela experiência (nunca "walk" numa estrada). */
  transport: "walk" | "bike" | "car" | "transit";
  /** O percurso pedido cabe nos dias disponíveis com qualidade? */
  feasible: boolean;
  /** Motivo, quando não é viável (para perguntar antes de gastar crédito). */
  reason?: string;
};

/**
 * Numa estrada linear, N dias significam N etapas de condução e, por isso,
 * N+1 pontos (partida + um fim de etapa por dia). Os restantes tipos mantêm
 * um ponto/base por dia.
 */
function roadStagePoints(anchors: string[], days: number): string[] {
  return evenlySpaced(anchors, Math.min(days + 1, anchors.length));
}

export type RoadStageRequirement = {
  day: number;
  stageEnd: string;
  from?: string;
  corridorTravelRequired: true;
  corridorWaypoints: string[];
  mealRequired: true;
  lodgingNearStageEndRequired: true;
  forbiddenStopCategories: readonly ["bus_station", "train_station", "transit_station"];
  forbidScheduleOrReservationAdvice: true;
};

/** A experiência faz-se em estrada (carro/mota) e não em comboio/estação. */
export function isRoadTemplate(template: CuratedRouteTemplate): boolean {
  return template.transport === "car";
}

const ROAD_TRANSIT_STOP_RE =
  /\b(?:bus|autocarro|coach|comboio|train|rail(?:way)?|metro|subway|esta[çc][ãa]o|station|terminal\s+rodovi[aá]rio)\b/iu;
const ROAD_SCHEDULE_ADVICE_RE =
  /\b(?:hor[aá]rios?|timetable|schedule|reserv(?:a|ar|e|ation)|bilhetes?|tickets?|plataforma|platform|embarque|boarding)\b/iu;

export function isRoadCompatibleStop(stop: { name: string; category?: string | null }): boolean {
  const category = (stop.category ?? "").toLocaleLowerCase();
  if (["bus_station", "train_station", "transit_station", "subway_station"].includes(category)) {
    return false;
  }
  return !ROAD_TRANSIT_STOP_RE.test(`${stop.name} ${stop.category ?? ""}`);
}

export function sanitizeRoadTravelAdvice(note: string | undefined): string | undefined {
  if (!note) return note;
  const kept = note
    .split(/(?<=[.!?])\s+/u)
    .filter((sentence) => !ROAD_TRANSIT_STOP_RE.test(sentence) && !ROAD_SCHEDULE_ADVICE_RE.test(sentence))
    .join(" ")
    .trim();
  return kept || undefined;
}

export function templateTransportMode(
  template: CuratedRouteTemplate,
): "walk" | "bike" | "car" | "transit" {
  switch (template.transport) {
    case "car":
      return "car";
    case "foot":
      return "walk";
    default:
      return "transit";
  }
}

/** Sub-sequência com `count` âncoras, mantendo início, fim e ordem. */
function evenlySpaced(anchors: string[], count: number): string[] {
  const n = anchors.length;
  if (count >= n) return [...anchors];
  if (count <= 1) return [anchors[0]];
  if (count === 2) return [anchors[0], anchors[n - 1]];
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    const index = Math.round((i * (n - 1)) / (count - 1));
    const city = anchors[index];
    if (!out.includes(city)) out.push(city);
  }
  // Arredondamentos podem colidir: completa com âncoras ainda não usadas.
  for (const city of anchors) {
    if (out.length >= count) break;
    if (!out.includes(city)) out.push(city);
  }
  out.sort((a, b) => anchors.indexOf(a) - anchors.indexOf(b));
  // Início e fim são obrigatórios no percurso completo.
  if (out[0] !== anchors[0]) out[0] = anchors[0];
  if (out[out.length - 1] !== anchors[n - 1]) out[out.length - 1] = anchors[n - 1];
  return out;
}

function kmBetween(template: CuratedRouteTemplate, from: string, to: string): number | undefined {
  const segments = template.roadSegments ?? [];
  const anchors = template.anchors;
  const fromIndex = anchors.findIndex((a) => fold(a) === fold(from));
  const toIndex = anchors.findIndex((a) => fold(a) === fold(to));
  if (fromIndex < 0 || toIndex < 0 || toIndex <= fromIndex) return undefined;
  let total = 0;
  let matched = 0;
  for (let i = fromIndex; i < toIndex; i++) {
    const a = anchors[i];
    const b = anchors[i + 1];
    const segment = segments.find(
      (s) =>
        (fold(s.from) === fold(a) && fold(s.to) === fold(b)) ||
        // Segmentos curados podem saltar âncoras intermédias.
        (anchors.findIndex((x) => fold(x) === fold(s.from)) <= i &&
          anchors.findIndex((x) => fold(x) === fold(s.to)) >= i + 1),
    );
    if (segment?.approxKm) {
      total += segment.approxKm;
      matched += 1;
    }
  }
  if (matched === 0) return undefined;
  return Math.round(total);
}

function segmentName(template: CuratedRouteTemplate, from: string, to: string): string | undefined {
  return template.roadSegments?.find((s) => fold(s.from) === fold(from) || fold(s.to) === fold(to))
    ?.name;
}

/** Índice da âncora mais próxima (por nome) de uma região/cidade de partida. */
function startIndexFor(template: CuratedRouteTemplate, startRegion?: string): number {
  const value = fold(startRegion ?? "");
  if (!value) return 0;
  const index = template.anchors.findIndex(
    (anchor) => value.includes(fold(anchor)) || fold(anchor).includes(value),
  );
  return index >= 0 ? index : 0;
}

export function buildRouteSkeleton(input: {
  template: CuratedRouteTemplate;
  days: number;
  mode: "full" | "partial";
  /** Região/cidade de partida do utilizador (só usada em troço parcial). */
  startRegion?: string;
}): RouteSkeleton {
  const { template, mode } = input;
  const anchors = template.anchors.filter(Boolean);
  const days = Math.max(1, Math.floor(input.days || 1));
  const transport = templateTransportMode(template);

  let cities: string[];
  let feasible = true;
  let reason: string | undefined;

  if (mode === "full") {
    // Cada dia avança para a âncora seguinte; nunca se repete uma cidade só
    // para encher dias. Com menos dias que âncoras, escolhem-se paragens
    // intermédias mantendo início e fim.
    const minDays = Math.max(2, template.minTripDays ?? 2);
    if (days < minDays || days < 2) {
      feasible = false;
      reason = `O percurso completo ${anchors[0]} → ${anchors[anchors.length - 1]} precisa de pelo menos ${minDays} dias para ser feito com qualidade.`;
    }
    const routePoints = isRoadTemplate(template)
      ? roadStagePoints(anchors, days)
      : evenlySpaced(anchors, Math.min(Math.max(2, days), anchors.length));
    cities = isRoadTemplate(template) ? routePoints.slice(1) : routePoints;
    if (days > cities.length) {
      // Mais dias do que paragens curadas: usamos paragens opcionais reais
      // antes de aceitar repetições (que nunca acontecem).
      const extras = (template.optionalPlaces ?? []).filter(
        (place) => !cities.some((city) => fold(city) === fold(place)),
      );
      for (const extra of extras) {
        if (cities.length >= days) break;
        cities.splice(Math.max(1, cities.length - 1), 0, extra);
      }
    }
  } else {
    const start = startIndexFor(template, input.startRegion);
    const span = Math.max(2, Math.min(days, anchors.length - start));
    cities = anchors.slice(start, start + span);
    if (cities.length < 2) cities = anchors.slice(Math.max(0, anchors.length - 2));
  }

  const legs: RouteSkeletonLeg[] = cities.map((city, index) => {
    const from = isRoadTemplate(template) && mode === "full"
      ? (index === 0 ? anchors[0] : cities[index - 1])
      : (index === 0 ? undefined : cities[index - 1]);
    return {
      day: index + 1,
      city,
      from,
      approxKm: from ? kmBetween(template, from, city) : undefined,
      segmentName: from ? segmentName(template, from, city) : undefined,
    };
  });

  return { templateId: template.id, mode, cities, legs, transport, feasible, reason };
}

/**
 * Contrato diário de qualquer estrada curada. As âncoras entre duas bases são
 * preservadas como pontos do corredor, mesmo quando o esqueleto compacta uma
 * viagem longa em menos dias. O primeiro dia inclui a partida oficial.
 */
export function buildRoadStageRequirements(
  template: CuratedRouteTemplate,
  skeleton: RouteSkeleton,
): RoadStageRequirement[] {
  if (!isRoadTemplate(template)) return [];
  return skeleton.legs.map((leg) => {
    const fromIndex = leg.from
      ? template.anchors.findIndex((anchor) => fold(anchor) === fold(leg.from ?? ""))
      : -1;
    const toIndex = template.anchors.findIndex((anchor) => fold(anchor) === fold(leg.city));
    const corridorWaypoints =
      fromIndex >= 0 && toIndex >= fromIndex
        ? template.anchors.slice(fromIndex, toIndex + 1)
        : [leg.city];
    return {
      day: leg.day,
      stageEnd: leg.city,
      from: leg.from,
      corridorTravelRequired: true,
      corridorWaypoints: corridorWaypoints.length ? corridorWaypoints : [leg.city],
      mealRequired: true,
      lodgingNearStageEndRequired: true,
      forbiddenStopCategories: ["bus_station", "train_station", "transit_station"],
      forbidScheduleOrReservationAdvice: true,
    };
  });
}

/** Nenhuma cidade pode repetir-se ao longo do esqueleto. */
export function hasRepeatedCities(cities: string[]): boolean {
  const seen = new Set<string>();
  for (const city of cities) {
    const key = fold(city);
    if (seen.has(key)) return true;
    seen.add(key);
  }
  return false;
}

/** Vocabulário correto por tipo de experiência (nunca "estação" numa estrada). */
export function experienceWording(template: CuratedRouteTemplate): {
  category: string;
  boardLabel: (place: string) => string;
  boardNote: string;
  segmentNote: (from: string, to: string) => string;
  travelLabel: string;
} {
  if (isRoadTemplate(template)) {
    return {
      category: "scenic_road",
      boardLabel: (place) => `Partida de ${place} pela ${template.name}`,
      boardNote:
        "Abastece, confirma o estado do tempo e segue pela estrada original da experiência.",
      segmentNote: (from, to) =>
        `Etapa rodoviária da ${template.name}: ${from} → ${to}. Conduz pela estrada original, com paragens ao longo do percurso.`,
      travelLabel: "estrada",
    };
  }
  if (template.transport === "foot") {
    return {
      category: "trail",
      boardLabel: (place) => `Início da etapa em ${place}`,
      boardNote: "Prepara água, calçado e horários antes de começar a etapa.",
      segmentNote: (from, to) => `Etapa a pé da ${template.name}: ${from} → ${to}.`,
      travelLabel: "a pé",
    };
  }
  return {
    category: "scenic_train",
    boardLabel: (place) => `Deslocação até à estação de ${place}`,
    boardNote:
      "Reserva margem para chegar à estação de partida, localizar a plataforma e embarcar.",
    segmentNote: (from, to) => `Embarque na estação de ${from}; chegada à estação de ${to}.`,
    travelLabel: "comboio",
  };
}
